import { Routes, Route, Navigate } from "react-router-dom";
import { ProtectedRoute, PublicRoute } from "./components/ProtectedRoute";
import { AppLayout } from "./components/layout/AppLayout";
import { useAuth } from "./context/AuthContext";

import Login from "./pages/Login/Login";
import Register from "./pages/Register/Register";
import ForgotPassword from "./pages/ForgotPassword/ForgotPassword";
import ResetPassword from "./pages/ResetPassword/ResetPassword";
import CambioPassword from "./pages/CambioPassword/CambioPassword";
import Dashboard from "./pages/Dashboard/Dashboard";
import CalendarioUnificato from "./pages/Calendario/CalendarioUnificato";
import Schedule from "./pages/Schedule/Schedule";
import MapPage from "./pages/Map/MapPage";
import Statistics from "./pages/Statistics/Statistics";
import Sopralluoghi from "./pages/Sopralluoghi/Sopralluoghi";
import Catalogo from "./pages/Catalogo/Catalogo";
import Devices from "./pages/Devices/Devices";
import Magazzino from "./pages/Magazzino/Magazzino";
import Contratti from "./pages/Contratti/Contratti";
import RegistroAffari from "./pages/RegistroAffari/RegistroAffari";
import DeviceDetail from "./pages/Devices/DeviceDetail";
import Activations from "./pages/Activations/Activations";
import Interventions from "./pages/Interventions/Interventions";
import Clients from "./pages/Clients/Clients";
import SedeDetail from "./pages/Sedi/SedeDetail";
import StatoLavori from "./pages/StatoLavori/StatoLavori";
import PraticaDettaglio from "./pages/StatoLavori/PraticaDettaglio";
import Users from "./pages/Settings/Users/Users";
import Privacy from "./pages/Settings/Privacy/Privacy";
import AuditLog from "./pages/AuditLog/AuditLog";
import OrdiniReport from "./pages/Reports/OrdiniReport";
import ImportRitiri from "./pages/RitiriValori/ImportRitiri";
import RitiriValori from "./pages/RitiriValori/RitiriValori";
import EmailTemplates from "./pages/Settings/EmailTemplates/EmailTemplates";
import TipiReport from "./pages/Settings/TipiReport/TipiReport";
import SystemSettings from "./pages/Settings/SystemSettings/SystemSettings";
import NotFound from "./pages/NotFound/NotFound";

/**
 * La home "/" mostra la Dashboard, che però il ruolo cliente non può vedere
 * (niente dashboard:read). Per il cliente la landing è il Calendario unificato,
 * che aggrega i suoi dati reali (installazioni/sopralluoghi/…). Gli altri ruoli
 * restano sulla Dashboard.
 */
function HomeRoute() {
  const { user } = useAuth();
  if (user?.role === "cliente") return <Navigate to="/calendario" replace />;
  return <Dashboard />;
}

export default function App() {
  return (
    <Routes>
      {/* --- Route pubbliche --------------------------------------------- */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />

      {/* Protetta ma fuori da AppLayout: con la password temporanea il backend
          rifiuta le API che il layout chiama al montaggio. */}
      <Route
        path="/cambio-password"
        element={
          <ProtectedRoute>
            <CambioPassword />
          </ProtectedRoute>
        }
      />

      {/* --- Route protette, tutte all'interno di AppLayout --------------- */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout>
              <HomeRoute />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/calendario"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CalendarioUnificato />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Stato lavori (issue #211): solo admin e staff, con `stato_lavori:read`. */}
      <Route
        path="/stato-lavori"
        element={
          <ProtectedRoute permission="stato_lavori:read">
            <AppLayout>
              <StatoLavori />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stato-lavori/:id"
        element={
          <ProtectedRoute permission="stato_lavori:read">
            <AppLayout>
              <PraticaDettaglio />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/schedule"
        element={
          <ProtectedRoute
            permission="installazioni:read"
            allowedRoles={["admin", "staff", "centro_assistenza", "operatore_tecnico"]}
          >
            <AppLayout>
              <Schedule />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/map"
        element={
          <ProtectedRoute permission="mappa:read">
            <AppLayout>
              <MapPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/stats"
        element={
          <ProtectedRoute permission="statistiche:read">
            <AppLayout>
              <Statistics />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/sopralluoghi"
        element={
          <ProtectedRoute permission="sopralluoghi:read">
            <AppLayout>
              <Sopralluoghi />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/catalogo"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Catalogo />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Parco device fisico. Elenco e dettaglio sono due rotte e non un
          pannello laterale come negli altri moduli: la scheda di una
          cassaforte è lunga (storico clienti, eventi, tre elenchi di record
          collegati) e soprattutto deve avere un URL, perché ci si arriva da
          fuori — dalla card Dispositivi della Vista Sede. Stessa scelta già
          fatta per /sedi/:id. */}
      <Route
        path="/devices"
        element={
          <ProtectedRoute permission="devices:read">
            <AppLayout>
              <Devices />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/devices/:id"
        element={
          <ProtectedRoute permission="devices:read">
            <AppLayout>
              <DeviceDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Contratti di Cash Management (PR B). Solo admin e staff: il permesso
          e' gia' il filtro, e la voce di menu non compare agli altri ruoli.
          La generazione del PDF e' ferma in attesa di una decisione sul
          formato sorgente — il resto della scheda (schede allegate, firme,
          archiviazione del firmato) funziona. */}
      <Route
        path="/contratti"
        element={
          <ProtectedRoute permission="contratti:read">
            <AppLayout>
              <Contratti />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Scheda contratto: pagina intera con un URL suo, come /clients/:id. */}
      <Route
        path="/contratti/:id"
        element={
          <ProtectedRoute permission="contratti:read">
            <AppLayout>
              <Contratti />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Registro Giornale degli Affari (Fase 5). Stesso perimetro dei
          contratti — admin e staff — perche' e' l'altra faccia dello stesso
          lavoro: le righe nascono dai contratti firmati. */}
      <Route
        path="/registro-affari"
        element={
          <ProtectedRoute permission="registro_affari:read">
            <AppLayout>
              <RegistroAffari />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Magazzino (Fase 4 punto 8). In sola lettura per ora: le rotte di
          scrittura esistono, il form no. */}
      <Route
        path="/magazzino"
        element={
          <ProtectedRoute permission="magazzino:read">
            <AppLayout>
              <Magazzino />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/activations"
        element={
          <ProtectedRoute permission="attivazioni:read">
            <AppLayout>
              <Activations />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/interventions"
        element={
          <ProtectedRoute permission="interventi:read">
            <AppLayout>
              <Interventions />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/clients"
        element={
          <ProtectedRoute permission="clienti_sedi:read">
            <AppLayout>
              <Clients />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Scheda cliente. Ha un URL suo perché da qui si apre una sede, e
          "Indietro" dalla sede deve riportare al cliente e non all'elenco. Stesso
          componente dell'elenco: la scheda ne era già una vista. */}
      <Route
        path="/clients/:id"
        element={
          <ProtectedRoute permission="clienti_sedi:read">
            <AppLayout>
              <Clients />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Vista Sede. Rotta piatta e non annidata sotto il cliente: una sede
          può essere spostata sotto un'altra azienda madre (PATCH
          /api/sedi/:id/sposta), e un URL che contenesse il cliente diventerebbe
          falso quel giorno, con i link salvati che porterebbero a un
          accostamento sbagliato. */}
      <Route
        path="/sedi/:id"
        element={
          <ProtectedRoute permission="clienti_sedi:read">
            <AppLayout>
              <SedeDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/users"
        element={
          <ProtectedRoute permission="utenti:read">
            <AppLayout>
              <Users />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Report Ordini OdA/OdV (amministrazione). Solo admin: il permesso e'
          gia' il filtro, e la voce di menu non compare agli altri ruoli — anche
          a staff, al contrario del resto dell'Amministrazione. Il motivo sta
          accanto alla chiave in `auth/permissions.ts`: l'elenco dice quali
          fatture aspettiamo e quali dobbiamo emettere, su tutti i clienti
          insieme. */}
      <Route
        path="/reports/ordini"
        element={
          <ProtectedRoute permission="report_ordini:read">
            <AppLayout>
              <OrdiniReport />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/ritiri-valori"
        element={
          <ProtectedRoute permission="ritiri:read">
            <AppLayout>
              <RitiriValori />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ritiri-valori/import"
        element={
          <ProtectedRoute permission="ritiri:import">
            <AppLayout>
              <ImportRitiri />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/audit-log"
        element={
          <ProtectedRoute permission="registro_attivita:read">
            <AppLayout>
              <AuditLog />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/email-templates"
        element={
          <ProtectedRoute permission="email_templates:read">
            <AppLayout>
              <EmailTemplates />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/tipi-report"
        element={
          <ProtectedRoute permission="report:configura">
            <AppLayout>
              <TipiReport />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/system"
        element={
          <ProtectedRoute permission="impostazioni:gestione">
            <AppLayout>
              <SystemSettings />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/privacy"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Privacy />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* --- 404 ------------------------------------------------------------ */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
